/**
 * HTTP layer for the Dayvise client protocol (prompt.md §47, §75).
 *
 * WHY this is hand-written rather than generated: the protocol is small and the
 * behaviours that matter are not expressible in a schema — bearer token from an
 * injected provider (never from a global), the workspace header that selects the
 * tenant, correlation-id capture, cancellation, and a retry policy that is
 * deliberately restricted to idempotent GETs so a duplicate `POST /messages`
 * can never start a second run.
 *
 * The `fetch` implementation is injectable and typed structurally, so the same
 * code runs on web (DOM fetch), in Tauri (DOM fetch), on Android via the Kotlin
 * client, and in tests against a stub.
 */

import {
  DayviseApiError,
  isAbortError,
  statusToErrorCode,
  statusToMessage,
} from "./errors.ts";
import { DEFAULT_RETRY_POLICY, abortError, delay, retryDelay, type RetryPolicy } from "./retry.ts";

/** Minimal request shape that both DOM and Workers `fetch` accept. */
export interface HttpRequestInit {
  readonly method: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly body?: string;
  readonly signal?: AbortSignal;
}

/** Minimal response shape so the SDK does not depend on a DOM lib. */
export interface HttpResponseLike {
  readonly status: number;
  readonly ok: boolean;
  readonly statusText?: string;
  readonly headers: { get(name: string): string | null };
  text(): Promise<string>;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export type FetchLike = (url: string, init: HttpRequestInit) => Promise<HttpResponseLike>;

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface HttpRequestOptions {
  readonly method?: HttpMethod;
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | null | undefined>>;
  /** Sent as `Idempotency-Key`; the API deduplicates on it (see `send`). */
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
  /** Overrides the configured retry policy for this call. */
  readonly retry?: Partial<RetryPolicy>;
}

/** Observation emitted after every attempt, used for login/telemetry plumbing. */
export interface HttpObservation {
  readonly method: string;
  readonly url: string;
  readonly status: number;
  readonly requestId: string | null;
  readonly durationMs: number;
  readonly attempt: number;
}

export interface HttpTransportConfig {
  /** Base URL including the API version, e.g. `https://api.dayvise.com/v1`. */
  readonly baseUrl: string;
  readonly getToken?: () => Promise<string | null> | string | null;
  /** Workspace id sent as `x-dayvise-workspace`; the server authorises it. */
  readonly getWorkspace?: () => Promise<string | null> | string | null;
  readonly fetch?: FetchLike;
  readonly defaultHeaders?: Readonly<Record<string, string>>;
  readonly retry?: Partial<RetryPolicy>;
  readonly random?: () => number;
  readonly sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  readonly onResponse?: (observation: HttpObservation) => void;
  readonly now?: () => number;
}

export interface HttpTransport {
  readonly baseUrl: string;
  /** Performs a request and decodes the JSON body (or `undefined` for 204). */
  request<T>(path: string, options?: HttpRequestOptions): Promise<T>;
  /** Performs a request and returns the raw response, for file downloads. */
  requestRaw(path: string, options?: HttpRequestOptions): Promise<HttpResponseLike>;
  /** Builds an absolute URL for a path and query, honouring the base URL. */
  url(path: string, query?: HttpRequestOptions["query"]): string;
}

/** Response headers that carry a correlation id, most specific first. */
const REQUEST_ID_HEADERS = ["cf-ray", "x-dayvise-request-id", "x-request-id"] as const;

export function joinUrl(baseUrl: string, path: string, query?: HttpRequestOptions["query"]): string {
  const base = baseUrl.replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const queryString = search.toString();
  return queryString.length > 0 ? `${base}${suffix}?${queryString}` : `${base}${suffix}`;
}

export function readRequestId(headers: { get(name: string): string | null }): string | null {
  for (const header of REQUEST_ID_HEADERS) {
    const value = headers.get(header);
    if (value !== null && value.length > 0) return value;
  }
  return null;
}

/**
 * Resolve the global `fetch`. The cast is unavoidable because the DOM and
 * Workers typings describe `fetch` differently while the runtime contract we
 * use is identical; keeping it in one place means no other module casts.
 */
function defaultFetch(): FetchLike {
  const candidate = (globalThis as { fetch?: unknown }).fetch;
  if (typeof candidate !== "function") {
    throw new Error("no fetch implementation is available; pass one via `fetch`");
  }
  return candidate.bind(globalThis) as FetchLike;
}

export function createHttpTransport(config: HttpTransportConfig): HttpTransport {
  const baseUrl = config.baseUrl.replace(/\/+$/, "");
  const doFetch = config.fetch ?? defaultFetch();
  const random = config.random ?? Math.random;
  const sleep = config.sleep ?? delay;
  const now = config.now ?? Date.now;
  const policy: RetryPolicy = { ...DEFAULT_RETRY_POLICY, ...config.retry };

  async function resolvedHeaders(options: HttpRequestOptions): Promise<Record<string, string>> {
    const headers: Record<string, string> = { accept: "application/json", ...config.defaultHeaders };
    const token = config.getToken ? await config.getToken() : null;
    if (token) headers.authorization = `Bearer ${token}`;
    const workspace = config.getWorkspace ? await config.getWorkspace() : null;
    if (workspace) headers["x-dayvise-workspace"] = workspace;
    if (options.body !== undefined) headers["content-type"] = "application/json";
    if (options.idempotencyKey) headers["idempotency-key"] = options.idempotencyKey;
    for (const [key, value] of Object.entries(options.headers ?? {})) headers[key] = value;
    return headers;
  }

  function isRetryable(method: string, status: number): boolean {
    // Only GETs may be retried automatically: everything else in this API has a
    // side effect (sending a message, steering a run, promoting memory).
    if (method !== "GET") return false;
    return status === 408 || status === 425 || status === 429 || status >= 500;
  }

  async function attemptOnce(url: string, method: string, options: HttpRequestOptions, attempt: number): Promise<HttpResponseLike> {
    if (options.signal?.aborted) throw abortError();
    const headers = await resolvedHeaders(options);
    if (options.signal?.aborted) throw abortError();
    const init: HttpRequestInit = {
      method,
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    };
    const startedAt = now();
    let response: HttpResponseLike;
    try {
      response = await doFetch(url, init);
    } catch (error) {
      if (isAbortError(error)) throw error;
      const requestId = null;
      config.onResponse?.({ method, url, status: 0, requestId, durationMs: now() - startedAt, attempt });
      throw new DayviseApiError({
        status: 0,
        code: "network_error",
        message: "the request could not reach Dayvise",
        requestId,
        retryable: true,
        cause: error,
      });
    }
    const requestId = readRequestId(response.headers);
    config.onResponse?.({ method, url, status: response.status, requestId, durationMs: now() - startedAt, attempt });
    if (!response.ok) {
      throw await toApiError(response, requestId);
    }
    return response;
  }

  async function sendRaw(path: string, options: HttpRequestOptions): Promise<HttpResponseLike> {
    const method = options.method ?? "GET";
    const url = joinUrl(baseUrl, path, options.query);
    const retry: RetryPolicy = { ...policy, ...options.retry };
    let attempt = 0;
    for (;;) {
      try {
        return await attemptOnce(url, method, options, attempt);
      } catch (error) {
        const retryable = error instanceof DayviseApiError ? error.retryable : false;
        const aborted = isAbortError(error);
        if (aborted || !retryable || !isRetryable(method, error instanceof DayviseApiError ? error.status : 0)) throw error;
        attempt += 1;
        if (attempt >= retry.maxAttempts) throw error;
        await sleep(retryDelay(attempt - 1, retry, random), options.signal);
      }
    }
  }

  return {
    baseUrl,
    url: (path, query) => joinUrl(baseUrl, path, query),
    requestRaw: sendRaw,
    async request<T>(path: string, options: HttpRequestOptions = {}): Promise<T> {
      const response = await sendRaw(path, options);
      if (response.status === 204) return undefined as T;
      const text = await response.text();
      if (text.length === 0) return undefined as T;
      try {
        return JSON.parse(text) as T;
      } catch (error) {
        throw new DayviseApiError({
          status: response.status,
          code: "invalid_response",
          message: "Dayvise returned a response the client could not read",
          requestId: readRequestId(response.headers),
          body: text.slice(0, 512),
          retryable: false,
          cause: error,
        });
      }
    },
  };
}

/** Turn a non-2xx response into a typed error, preferring the API's own code. */
async function toApiError(response: HttpResponseLike, requestId: string | null): Promise<DayviseApiError> {
  let body: unknown = null;
  let text = "";
  try {
    text = await response.text();
  } catch {
    text = "";
  }
  if (text.length > 0) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = null;
    }
  }
  const envelope = body as { error?: unknown; message?: unknown } | null;
  const code = typeof envelope?.error === "string" && envelope.error.length > 0 ? envelope.error : statusToErrorCode(response.status);
  const message =
    typeof envelope?.message === "string" && envelope.message.length > 0
      ? envelope.message
      : statusToMessage(response.status, response.statusText ?? "");
  return new DayviseApiError({ status: response.status, code, message, requestId, body });
}
